import React from 'react';

import { DOCUSIGN, SECURE_BRAND_ANIM } from '../../../../theme/tokens';
import { PEN_ICON, SIGNATURE, WRITING_PEN } from '../pen-glyph';
import { Check, CheckDisc, CheckMark, Picture, Sheet, Signature } from './SigningPicture.styles';

export type SigningPictureProps = {
  /** When the pen arrives, in ms from mount. */
  delayMs: number;
  /** Reduced motion: the page as it ends, signed and checked, with no pen. */
  still: boolean;
};

const { PEN_IN_MS, SIGN_DRAW_MS, PEN_OUT_MS, CHECK_MS } = SECURE_BRAND_ANIM;
/** The check pops a breath after the last stroke, as the pen lifts away. */
const CHECK_AT_MS = PEN_IN_MS + SIGN_DRAW_MS + PEN_OUT_MS * 0.4;
const END_MS = CHECK_AT_MS + CHECK_MS;
/** How far the pen travels as it arrives and as it lifts away (up and to the right). */
const PEN_TRAVEL = { x: 10, y: -12 };

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));
const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;
/** A hand's pace: slow to start, steady through the middle, easing off at the end. */
const easeInOutSine = (t: number): number => -(Math.cos(Math.PI * t) - 1) / 2;
/** Overshoots a little and settles: the check popping into place. */
const easeOutBack = (t: number): number => 1 + 2.2 * (t - 1) ** 3 + 1.2 * (t - 1) ** 2;

/**
 * Step 3's picture: a page with DocuSign's yellow tag on the signature line, and a pen that writes the
 * signature on it, ink following the tip. The tag goes once the signature is down (as on DocuSign's
 * page), the pen lifts away, and a check pops on the page's corner. Played once from `delayMs`; a new
 * mount plays it again. Decorative: the step's own line says what to do.
 *
 * One `requestAnimationFrame` timeline drives it all, writing to the elements directly: the ink is the
 * path's dash offset (`pathLength` 1) and the pen sits on `getPointAtLength` of the same progress, so
 * the tip is always where the ink ends.
 */
export const SigningPicture = ({ delayMs, still }: SigningPictureProps): React.ReactElement => {
  const signature = React.useRef<SVGPathElement>(null);
  const pen = React.useRef<SVGGElement>(null);
  const tag = React.useRef<SVGGElement>(null);
  const check = React.useRef<SVGGElement>(null);

  // Plays the timeline on the elements, frame by frame, from `delayMs` on; stops at its last frame.
  React.useEffect(() => {
    const path = signature.current;
    const penEl = pen.current;
    const tagEl = tag.current;
    const checkEl = check.current;
    if (still || !path || !penEl || !tagEl || !checkEl) return undefined;
    const length = path.getTotalLength();

    const paint = (t: number): void => {
      const arrive = easeOutCubic(clamp01(t / PEN_IN_MS));
      const written = easeInOutSine(clamp01((t - PEN_IN_MS) / SIGN_DRAW_MS));
      const lift = easeOutCubic(clamp01((t - PEN_IN_MS - SIGN_DRAW_MS) / PEN_OUT_MS));
      const pop = clamp01((t - CHECK_AT_MS) / CHECK_MS);
      const tip = path.getPointAtLength(written * length);
      const away = 1 - arrive + lift;

      path.style.strokeDashoffset = String(1 - written);
      path.style.opacity = written > 0 ? '1' : '0';
      penEl.setAttribute('transform', `translate(${tip.x + away * PEN_TRAVEL.x} ${tip.y + away * PEN_TRAVEL.y})`);
      penEl.style.opacity = String(arrive * (1 - lift));
      tagEl.style.opacity = String(1 - lift);
      checkEl.style.opacity = String(clamp01(pop * 3));
      checkEl.style.transform = `scale(${0.4 + 0.6 * easeOutBack(pop)})`;
    };

    let frame = 0;
    let start: number | null = null;
    const tick = (now: number): void => {
      if (start === null) start = now;
      const t = now - start - delayMs;
      paint(t);
      if (t < END_MS) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [delayMs, still]);

  return (
    <Picture viewBox="0 0 240 148" aria-hidden="true" focusable="false">
      <Sheet x="40" y="20" width="160" height="150" rx="9" fill={DOCUSIGN.paper} stroke={DOCUSIGN.paperEdge} />
      <rect x="56" y="36" width="52" height="7" rx="3.5" fill={DOCUSIGN.ink} fillOpacity="0.85" />
      <rect x="56" y="52" width="128" height="4" rx="2" fill={DOCUSIGN.line} />
      <rect x="56" y="62" width="112" height="4" rx="2" fill={DOCUSIGN.line} />
      <rect x="56" y="72" width="120" height="4" rx="2" fill={DOCUSIGN.line} />
      <path d="M98 116h86" stroke={DOCUSIGN.rule} strokeWidth="1.4" strokeLinecap="round" />

      {/* DocuSign's yellow "sign here" tag, pointing at the line, with the pen icon on it. */}
      <g ref={tag} style={{ opacity: still ? 0 : 1 }}>
        <path d="M55 98h27l8 9-8 9H55a3 3 0 01-3-3v-12a3 3 0 013-3z" fill={DOCUSIGN.signTag} />
        <g transform="translate(59 99.5) scale(0.6)" fill="none" stroke={DOCUSIGN.ink} strokeWidth="2.2">
          <path d={PEN_ICON.pencil} strokeLinejoin="round" />
          <path d={PEN_ICON.line} strokeLinecap="round" />
        </g>
      </g>

      <Signature
        ref={signature}
        d={SIGNATURE}
        pathLength={1}
        style={still ? { strokeDashoffset: 0 } : { strokeDashoffset: 1, opacity: 0 }}
      />

      <g ref={pen} style={{ opacity: 0 }}>
        <path
          d={WRITING_PEN.barrel}
          fill={DOCUSIGN.paper}
          stroke={DOCUSIGN.ink}
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d={WRITING_PEN.band} stroke={DOCUSIGN.ink} strokeWidth="1.5" />
        <path d={WRITING_PEN.tip} fill={DOCUSIGN.ink} />
      </g>

      <g transform="translate(186 34)">
        <Check ref={check} style={still ? undefined : { opacity: 0, transform: 'scale(0.4)' }}>
          <CheckDisc r="11" />
          <CheckMark d="M-5 .5l3.4 3.4 6.6-7" />
        </Check>
      </g>
    </Picture>
  );
};

export default SigningPicture;
